/**
 * Shown when a caller is authenticated but not entitled to this app. Deliberately explicit
 * about which account is signed in, and offers a way out — otherwise a user with the wrong
 * account is stuck on a dead end with no idea why.
 */
export function AccessDenied({
  appName,
  message,
  accountName,
  onSignOut,
}: {
  appName: string;
  message: string;
  accountName?: string | null;
  onSignOut?: (() => void) | null;
}) {
  return (
    <div className="grid min-h-screen place-items-center bg-ground p-6">
      <div
        className="w-full max-w-md border border-line bg-surface p-8 text-center"
        style={{ borderTopWidth: 'var(--panel-rule)', borderTopColor: 'var(--color-bad)' }}
      >
        <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-felt-50">
          {/* Inline so this package keeps no icon dependency. */}
          <svg
            className="size-6 text-bad"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <path d="M12 8v4" />
            <path d="M12 16h.01" />
          </svg>
        </div>
        <h1 className="font-display text-lg font-semibold text-ink-950">
          You don&apos;t have access to {appName}
        </h1>
        <p className="mt-2 text-sm text-ink-800">{message}</p>
        {accountName ? (
          <p className="mt-4 border border-line bg-felt-50 px-3 py-2 text-xs text-ink-800">
            Signed in as <span className="font-semibold text-ink-950">{accountName}</span>
          </p>
        ) : null}
        {onSignOut ? (
          <button
            type="button"
            onClick={onSignOut}
            className="mt-6 w-full border border-line py-2 text-xs font-bold uppercase tracking-[0.08em] text-ink-800 transition hover:bg-felt-50"
          >
            Sign out and use a different account
          </button>
        ) : null}
      </div>
    </div>
  );
}
